import { useCallback } from 'react';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { VITE_ENV } from 'src/constants/envs';
import type { tSessionUser } from 'src/providers/UserProvider/User.types';
import type { eUserRole } from 'src/providers/UserProvider/User.enums';
import type { tCurrentWorkspaceInfo } from 'src/providers/WorkspaceProvider/Workspace.types';
import { readStoredAttribution } from 'src/providers/WorkspaceProvider/Workspace.attribution';
import { aiAssistantOf } from 'src/providers/WorkspaceProvider/Workspace.aiAssistant';
import { AUTH_EVENTS } from './authAnalytics.constants';

/**
 * Union of every authentication analytics event name.
 */
export type tAuthEventName = (typeof AUTH_EVENTS)[keyof typeof AUTH_EVENTS];

// Workspaces with at least one member on a leadsales-owned email domain are
// flagged internal so dashboards can exclude them from external benchmarks.
// Heuristic only — Sales/CS demo workspaces created from external accounts
// will still register as external until we have an authoritative flag.
const INTERNAL_EMAIL_DOMAINS = ['@leadsales.io', '@leadsales.com'] as const;

// Workspace super properties that persist across reloads. They are dropped when
// the workspace changes so events sent before the new info loads never carry
// the previous workspace's values. `user_role` is the role in that workspace;
// identify registers it again once the new workspace's role settles.
const WORKSPACE_DETAIL_PROPERTY_KEYS = [
  'workspace_name',
  'workspace_member_count',
  'workspace_created_at',
  'workspace_industry',
  'workspace_is_internal',
  'workspace_bot_active',
  'user_role',
] as const;

// Durable store for the registration `user_type`. A plain localStorage key
// (not a PostHog super property) so it survives the mid-registration
// `posthog.reset()` and the AuthMiddleware hard reloads across `/auth/*`.
const USER_TYPE_STORAGE_KEY = 'register_user_type';

// Auth events that keep the auto-injected registration `user_type`. Only these
// steps report user_type implicitly; every other auth event emitted through
// `trackAuthEvent` no longer carries it. An explicit `user_type` in the payload
// still wins regardless of membership.
const USER_TYPE_AUTO_INJECT_EVENTS = new Set<tAuthEventName>([
  AUTH_EVENTS.EMAIL_STEP,
  AUTH_EVENTS.ERROR_ACCOUNT_ALREADY_EXIST,
]);

function readRegisteredUserType(): 'new' | 'old' | null {
  try {
    const value = localStorage.getItem(USER_TYPE_STORAGE_KEY);
    return value === 'new' || value === 'old' ? value : null;
  } catch {
    return null;
  }
}

function deriveWorkspaceIsInternal(
  workspaceInfo: tCurrentWorkspaceInfo | undefined,
): boolean {
  if (!workspaceInfo?.users) return false;
  return Object.values(workspaceInfo.users).some((u) => {
    const email = u.email?.toLowerCase();
    if (!email) return false;
    return INTERNAL_EMAIL_DOMAINS.some((d) => email.endsWith(d));
  });
}

// Signup-attribution person properties (plan U5). Read straight from
// localStorage — identifyUser runs before WorkspaceProvider state exists and
// after the mid-registration `posthog.reset()`, both of which the durable
// stash survives. PostHog auto-captures utm_* from the URL already; these
// carry the ls_* extras auto-capture never sees. First-touch keys ride the
// identify call's third arg ($set_once) so they are written exactly once per
// person; last-touch keys are regular person props. snake_case per the
// analytics adapter convention.
function buildAttributionPersonProps(): {
  props: Record<string, string>;
  onceProps: Record<string, string> | undefined;
} {
  const attribution = readStoredAttribution();
  if (!attribution) return { props: {}, onceProps: undefined };

  // GRO-1271: the last-touch utm_source and the assistant behind the signup,
  // so PostHog keeps the source past the mid-registration reset.
  const aiAssistant = aiAssistantOf(attribution);
  const props: Record<string, string> = {
    ...(attribution.utm_source && { attr_utm_source: attribution.utm_source }),
    ...(aiAssistant && { attr_ai_assistant: aiAssistant }),
    ...(attribution.ref && { attr_ref: attribution.ref }),
    ...(attribution.land && { attr_land: attribution.land }),
    ...(attribution.page && { attr_page: attribution.page }),
    ...(attribution.hutk && { attr_hutk: attribution.hutk }),
    ...(attribution.captured_at && { attr_captured_at: attribution.captured_at }),
  };

  const onceProps: Record<string, string> = {
    ...(attribution.ft_source && { attr_ft_source: attribution.ft_source }),
    ...(attribution.ft_medium && { attr_ft_medium: attribution.ft_medium }),
    ...(attribution.ft_campaign && { attr_ft_campaign: attribution.ft_campaign }),
    ...(attribution.ft_ref && { attr_ft_ref: attribution.ft_ref }),
    ...(attribution.ft_land && { attr_ft_land: attribution.ft_land }),
    ...(attribution.ft_date && { attr_ft_date: attribution.ft_date }),
  };

  return {
    props,
    onceProps: Object.keys(onceProps).length > 0 ? onceProps : undefined,
  };
}

export function useAnalyticsAuth() {
  const identifyUser = useCallback((user: tSessionUser, role?: eUserRole): void => {
    if (!user?.uuid) return;
    // Native SDK events (including survey responses) bypass useAnalytics().
    // Register before identify can refresh survey targeting; null clears a stale role.
    getAnalytics().register({ user_role: role ?? null });
    const attribution = buildAttributionPersonProps();
    const identifyProps = {
      $email: user.email,
      $name: user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
      user_auth_id: user.authId,
      first_name: user.firstName,
      last_name: user.lastName,
      ...(role && { user_role: role }),
      ...attribution.props,
    };

    // Third arg ($set_once) only when first-touch data exists — keeps the
    // no-attribution call shape identical to the historical two-arg form.
    if (attribution.onceProps) {
      getAnalytics().identify(user.uuid, identifyProps, attribution.onceProps);
    } else {
      getAnalytics().identify(user.uuid, identifyProps);
    }
  }, []);

  const registerWorkspaceContext = useCallback(
    (workspaceId: string | undefined, workspaceInfo?: tCurrentWorkspaceInfo): void => {
      if (!workspaceId) return;
      // A reload of the same workspace keeps its properties until the info loads.
      if (
        getAnalytics().getRegisteredProperty('workspace_id') !== workspaceId
      ) {
        WORKSPACE_DETAIL_PROPERTY_KEYS.forEach((key) =>
          getAnalytics().unregister(key),
        );
      }
      const memberCount = workspaceInfo?.users
        ? Object.keys(workspaceInfo.users).length
        : undefined;
      getAnalytics().register({
        workspace_id: workspaceId,
        ...(workspaceInfo?.name && { workspace_name: workspaceInfo.name }),
        ...(workspaceInfo?.createdAt && { workspace_created_at: workspaceInfo.createdAt }),
        ...(workspaceInfo?.industryType && { workspace_industry: workspaceInfo.industryType }),
        // Always emit even when 0 — undefined would keep a stale value from a
        // prior workspace as a super property.
        ...(memberCount !== undefined && { workspace_member_count: memberCount }),
        ...(workspaceInfo && {
          workspace_is_internal: deriveWorkspaceIsInternal(workspaceInfo),
        }),
        platform: 'WEB',
        environment: VITE_ENV,
      });
      // Group association: every subsequent event carries $groups.workspace so
      // workspace-level analysis works natively once Group Analytics is
      // enabled (and via properties meanwhile). Server-side emitters must
      // match this same group type ('workspace') — never use workspace_id as
      // a distinct_id.
      // The provider sends $groupidentify only for changed properties, a group
      // the SDK does not hold, or after the resend interval; every other call
      // re-asserts the group without an event.
      getAnalytics().group('workspace', workspaceId, {
        ...(workspaceInfo?.name && { name: workspaceInfo.name }),
        ...(workspaceInfo?.createdAt && { created_at: workspaceInfo.createdAt }),
        ...(workspaceInfo?.industryType && { industry: workspaceInfo.industryType }),
        ...(memberCount !== undefined && { member_count: memberCount }),
        ...(workspaceInfo && {
          is_internal: deriveWorkspaceIsInternal(workspaceInfo),
        }),
      });
    },
    [],
  );

  // Subscription and SmartLeadbot config load asynchronously after the
  // workspace context, so they live in their own providers/hooks. Each can
  // call `register({...})` directly via `getAnalytics()` — PostHog merges
  // super properties additively. This wrapper is exposed for symmetry and
  // centralized error handling without forcing those callers to import the
  // analytics service module directly.
  const registerWorkspaceProperties = useCallback(
    (properties: Record<string, unknown>): void => {
      getAnalytics().register(properties);
    },
    [],
  );

  const resetAnalytics = useCallback((): void => {
    getAnalytics().reset();
  }, []);

  const startSessionRecording = useCallback((): void => {
    getAnalytics().startSessionRecording();
  }, []);

  const stopSessionRecording = useCallback((): void => {
    getAnalytics().stopSessionRecording();
  }, []);

  // Emits an auth-domain event (login / register). Unlike `useAnalytics`, it
  // does NOT guard on a workspace id, because sign-up steps run before a
  // workspace exists. `user_role` is not attached per event: it is a registered
  // super property post-login, and pre-workspace sign-up has no role yet.
  // `user_type`, when it has been determined for this registration, is injected
  // from durable storage (see `registerUserType`) only for the events in
  // `USER_TYPE_AUTO_INJECT_EVENTS`; other steps no longer carry it. An explicit
  // `user_type` in the payload still wins.
  const trackAuthEvent = useCallback(
    (name: tAuthEventName, payload: Record<string, unknown> = {}): void => {
      const userType = USER_TYPE_AUTO_INJECT_EVENTS.has(name)
        ? readRegisteredUserType()
        : null;
      getAnalytics().track(name, {
        ...(userType ? { user_type: userType } : {}),
        ...payload,
      });
    },
    [],
  );

  // Records `user_type` for the current registration. It is stored in a plain
  // localStorage key rather than a PostHog super property because the sign-up
  // flow resets the analytics identity mid-registration (`onSignUpSubmit`
  // clears local state, which calls `posthog.reset()`), which would wipe any
  // super property — session- or persistent-scoped — set on an earlier step.
  // The plain key is untouched by `reset()` and survives the hard reloads
  // AuthMiddleware performs across `/auth/*` routes, so `user_type` reaches
  // every downstream step. `new` = the account did not exist before this
  // sign-up; `old` = the user was routed to the account-already-exists view.
  const registerUserType = useCallback((userType: 'new' | 'old'): void => {
    try {
      localStorage.setItem(USER_TYPE_STORAGE_KEY, userType);
    } catch {
      // Storage unavailable (private mode / quota): degrade to no user_type
      // rather than break the sign-up flow.
    }
  }, []);

  // Reads the `user_type` recorded for the current registration, for callers
  // that emit through the workspace-guarded hook (which cannot use
  // `trackAuthEvent`'s injection). Returns null when none was determined.
  const getRegisteredUserType = useCallback(
    (): 'new' | 'old' | null => readRegisteredUserType(),
    [],
  );

  // Clears the recorded `user_type` once a registration reaches its terminal
  // step, so a later funnel on the same browser starts clean.
  const clearRegisteredUserType = useCallback((): void => {
    try {
      localStorage.removeItem(USER_TYPE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return {
    identifyUser,
    registerWorkspaceContext,
    registerWorkspaceProperties,
    resetAnalytics,
    startSessionRecording,
    stopSessionRecording,
    trackAuthEvent,
    registerUserType,
    getRegisteredUserType,
    clearRegisteredUserType,
  };
}
